import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpenCheck,
  CheckCheck,
  Circle,
  CircleCheck,
  CircleCheckBig,
  ClipboardCheck,
  DollarSign,
  NotebookTabs,
  Trash2,
  UserCheck,
  UserRoundPlus,
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../app/layouts/MainLayout';
import { Button, cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import FullScreenModal from '../../../components/ui/Modal';
import SearchableSelect, { SearchableSelectOption } from '../../../components/ui/SearchableSelect';
import Toast, { ToastMessage } from '../../../components/ui/Toast';
import { AttendanceStatus } from '../../attendance/types/attendance.types';
import { useMagazines, useSegments } from '../../trimesters/hooks';
import { useTrimesters } from '../../trimesters/context/TrimestersContext';
import { ClassStudentRecord } from '../mocks/classStudents';
import { useClassStudents } from '../hooks/useClassStudents';
import { useClassLessonVisitors } from '../hooks/useClassLessonVisitors';
import { useEnsureClassroom } from '../hooks/useEnsureClassroom';
import { classLessonService, ClassLessonRecord } from '../services/classLesson.service';
import { classTeacherService } from '../services/classTeacher.service';
import { AssignedClassTeacher } from '../types/classroom.types';
import { buildClassLessonsFromMagazine, resolveClassMagazine } from '../utils/classLessonCatalog';

interface LessonAttendanceFormValues {
  title: string;
  date: string;
  teacherNames: string[];
  description: string;
  bibles: string;
  offeringAmount: string;
  isMissionsOffering: boolean;
  notes: string;
  studentStatuses: Record<string, AttendanceStatus>;
}

interface VisitorFormValues {
  name: string;
  whatsapp: string;
}

type LessonDetailsTab = 'summary' | 'attendance';

const todayIso = '2026-04-14';

const emptyVisitorFormValues: VisitorFormValues = {
  name: '',
  whatsapp: '',
};

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const calculateAge = (birthDate?: string) => {
  if (!birthDate) {
    return null;
  }

  const birth = new Date(`${birthDate}T00:00:00`);
  const today = new Date(`${todayIso}T00:00:00`);
  let age = today.getFullYear() - birth.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());

  return hasBirthdayPassed ? age : age - 1;
};

const formatDateLabel = (value?: string | null) => {
  if (!value) {
    return 'Não informado';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const formatCurrencyInput = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0);

const parseCurrencyInput = (value: string) => {
  const normalized = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const buildAttendanceStatusMap = (
  students: ClassStudentRecord[],
  existingStatuses?: Array<{ studentId: string; status: AttendanceStatus }>
) =>
  students.reduce<Record<string, AttendanceStatus>>((accumulator, student) => {
    accumulator[student.id] = existingStatuses?.find((item) => item.studentId === student.id)?.status ?? 'absent';
    return accumulator;
  }, {});

const resolveRouteLessonNumber = (value?: string) => {
  const match = value?.match(/(\d+)(?!.*\d)/);
  return match ? Number(match[1]) : Number.NaN;
};

const ClassLessonDetailsPage = () => {
  const { classId, lessonId } = useParams<{ classId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { segments } = useSegments();
  const { currentTrimesterId, setCurrentTrimesterId, trimesters } = useTrimesters();
  const routeTrimesterId = searchParams.get('trimester_id') ?? '';
  const activeTrimesterId = routeTrimesterId || currentTrimesterId;
  const { magazines, isLoading: isLoadingMagazines, error: magazinesError } = useMagazines(activeTrimesterId);
  const {
    classroom: selectedClass,
    isLoadingClassroom,
    classroomError,
  } = useEnsureClassroom(classId, activeTrimesterId);

  const [activeLessonTab, setActiveLessonTab] = useState<LessonDetailsTab>('summary');
  const [visitorFormValues, setVisitorFormValues] = useState<VisitorFormValues>(emptyVisitorFormValues);
  const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
  const [isSavingVisitor, setIsSavingVisitor] = useState(false);
  const [removingVisitorId, setRemovingVisitorId] = useState<string | null>(null);
  const [visitorRemovalId, setVisitorRemovalId] = useState<string | null>(null);
  const [visitorConversionId, setVisitorConversionId] = useState<string | null>(null);
  const [convertingVisitorId, setConvertingVisitorId] = useState<string | null>(null);
  const [saveVisitorError, setSaveVisitorError] = useState<string | null>(null);
  const [visitorNotice, setVisitorNotice] = useState<ToastMessage | null>(null);

  useEffect(() => {
    if (!routeTrimesterId || routeTrimesterId === currentTrimesterId) {
      return;
    }

    const trimesterExists = trimesters.some((trimester) => trimester.id === routeTrimesterId);
    if (trimesterExists) {
      setCurrentTrimesterId(routeTrimesterId);
    }
  }, [currentTrimesterId, routeTrimesterId, setCurrentTrimesterId, trimesters]);

  const {
    students: selectedClassStudents,
    isLoading: isLoadingStudents,
    reloadStudents,
  } = useClassStudents(selectedClass?.id, activeTrimesterId);
  const [classLessonRecords, setClassLessonRecords] = useState<ClassLessonRecord[]>([]);
  const [classTeachers, setClassTeachers] = useState<AssignedClassTeacher[]>([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState(false);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [lessonError, setLessonError] = useState<string | null>(null);
  const [teachersError, setTeachersError] = useState<string | null>(null);
  const [isSavingLesson, setIsSavingLesson] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (!selectedClass?.id) {
      setClassLessonRecords([]);
      setLessonError(null);
      setIsLoadingLessons(false);
      return () => {
        mounted = false;
      };
    }

    setIsLoadingLessons(true);
    setLessonError(null);

    classLessonService
      .listByClass(selectedClass.id, activeTrimesterId)
      .then((items) => {
        if (!mounted) {
          return;
        }

        setClassLessonRecords(items);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        setLessonError(error instanceof Error ? error.message : 'Nao foi possivel carregar a frequência da lição.');
      })
      .finally(() => {
        if (mounted) {
          setIsLoadingLessons(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeTrimesterId, selectedClass?.id]);

  useEffect(() => {
    let mounted = true;

    if (!selectedClass?.id) {
      setClassTeachers([]);
      setTeachersError(null);
      setIsLoadingTeachers(false);
      return () => {
        mounted = false;
      };
    }

    setIsLoadingTeachers(true);
    setTeachersError(null);

    classTeacherService
      .listByClass(selectedClass.id, activeTrimesterId)
      .then((items) => {
        if (!mounted) {
          return;
        }

        setClassTeachers(items);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        setTeachersError(error instanceof Error ? error.message : 'Nao foi possivel carregar os professores da turma.');
      })
      .finally(() => {
        if (mounted) {
          setIsLoadingTeachers(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeTrimesterId, selectedClass?.id]);

  const selectedClassMagazine = useMemo(
    () => resolveClassMagazine(selectedClass, activeTrimesterId, magazines, segments),
    [activeTrimesterId, magazines, segments, selectedClass]
  );
  const classLessons = useMemo(
    () =>
      selectedClass && !isLoadingMagazines
        ? buildClassLessonsFromMagazine(selectedClass, selectedClassMagazine, classLessonRecords)
        : [],
    [classLessonRecords, isLoadingMagazines, selectedClass, selectedClassMagazine]
  );
  const lesson = useMemo(() => {
    const lessonNumber = resolveRouteLessonNumber(lessonId);

    return (
      classLessons.find((item) => item.id === lessonId) ??
      classLessons.find((item) => Number.isFinite(lessonNumber) && item.lessonNumber === lessonNumber) ??
      null
    );
  }, [classLessons, lessonId]);
  const attendanceEntry = useMemo(() => {
    if (!lesson) {
      return null;
    }

    return classLessonRecords.find((item) => item.id === lesson.id || item.lessonNumber === lesson.lessonNumber) ?? null;
  }, [classLessonRecords, lesson]);
  const {
    classVisitors,
    isLoading: isLoadingVisitors,
    error: visitorsError,
    createVisitor,
    removeVisitor,
    convertVisitorToStudent,
  } = useClassLessonVisitors(selectedClass?.id, lesson?.id, activeTrimesterId);

  const [formValues, setFormValues] = useState<LessonAttendanceFormValues>({
    title: '',
    date: todayIso,
    teacherNames: [],
    description: '',
    bibles: '0',
    offeringAmount: formatCurrencyInput(0),
    isMissionsOffering: false,
    notes: '',
    studentStatuses: {},
  });

  useEffect(() => {
    if (!lesson || !selectedClass) {
      return;
    }

    setFormValues({
      title: lesson.title,
      date: lesson.date || todayIso,
      teacherNames: lesson.teacherName ? [lesson.teacherName] : classTeachers.slice(0, 1).map((teacher) => teacher.name),
      description: lesson.description,
      bibles: String(attendanceEntry?.biblesCount ?? 0),
      offeringAmount: formatCurrencyInput(attendanceEntry?.offeringAmount ?? 0),
      isMissionsOffering: attendanceEntry?.isMissionsOffering ?? false,
      notes: attendanceEntry?.notes ?? '',
      studentStatuses: buildAttendanceStatusMap(
        selectedClassStudents,
        attendanceEntry?.studentAttendances?.map((item) => ({
          studentId: item.studentId,
          status: item.present ? 'present' : 'absent',
        }))
      ),
    });
  }, [attendanceEntry, classTeachers, lesson, selectedClass, selectedClassStudents]);

  const expectedAgeRange = useMemo(() => {
    if (!selectedClass) {
      return null;
    }

    const classLabel = normalizeText(`${selectedClass.category} ${selectedClass.ageRange}`);
    const matchedSegment = segments.find((segment) => classLabel.includes(normalizeText(segment.name)));

    if (matchedSegment) {
      return {
        label: `${matchedSegment.ageStart} a ${matchedSegment.ageEnd} anos`,
        start: matchedSegment.ageStart,
        end: matchedSegment.ageEnd,
      };
    }

    const plusMatch = selectedClass.ageRange.match(/(\d+)\s*\+/);
    if (plusMatch) {
      return { label: selectedClass.ageRange, start: Number(plusMatch[1]), end: 120 };
    }

    const rangeMatch = selectedClass.ageRange.match(/(\d+)\D+(\d+)/);
    if (rangeMatch) {
      return { label: selectedClass.ageRange, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
    }

    return null;
  }, [selectedClass, segments]);

  const getAgeMismatchLabel = useCallback(
    (student: ClassStudentRecord) => {
      const age = calculateAge(student.birthDate);

      if (!expectedAgeRange || age === null || (age >= expectedAgeRange.start && age <= expectedAgeRange.end)) {
        return null;
      }

      return `Fora da faixa etária: ${age} anos; esperado ${expectedAgeRange.label}`;
    },
    [expectedAgeRange]
  );

  const lessonVisitors = classVisitors;
  const visitorToRemove = visitorRemovalId ? lessonVisitors.find((visitor) => visitor.id === visitorRemovalId) ?? null : null;
  const visitorToConvert = visitorConversionId ? lessonVisitors.find((visitor) => visitor.id === visitorConversionId) ?? null : null;

  const attendanceSummary = selectedClassStudents.reduce(
    (accumulator, student) => {
      const status = formValues.studentStatuses[student.id] ?? 'absent';

      if (status === 'present') {
        accumulator.present += 1;
      } else {
        accumulator.absent += 1;
      }

      return accumulator;
    },
    { present: 0, absent: 0 }
  );
  const biblesCount = Math.max(0, Number(formValues.bibles) || 0);
  const offeringAmount = parseCurrencyInput(formValues.offeringAmount);

  const teacherOptions: SearchableSelectOption[] = useMemo(
    () => {
      const options = classTeachers.map((teacher) => ({
        value: teacher.name,
        label: teacher.name,
        description: teacher.role,
      }));

      const selectedTeacherName = formValues.teacherNames[0];
      if (selectedTeacherName && !options.some((option) => option.value === selectedTeacherName)) {
        return [
          {
            value: selectedTeacherName,
            label: selectedTeacherName,
            description: 'Professor registrado na aula',
          },
          ...options,
        ];
      }

      return options;
    },
    [classTeachers, formValues.teacherNames]
  );

  const handleAttendanceStatusChange = (studentId: string, status: AttendanceStatus) => {
    setFormValues((current) => ({
      ...current,
      studentStatuses: {
        ...current.studentStatuses,
        [studentId]: status,
      },
    }));
  };

  const handleFillAttendance = (status: AttendanceStatus) => {
    setFormValues((current) => ({
      ...current,
      studentStatuses: selectedClassStudents.reduce<Record<string, AttendanceStatus>>((accumulator, student) => {
        accumulator[student.id] = status;
        return accumulator;
      }, {}),
    }));
  };

  const handleSaveVisitor = async () => {
    if (!selectedClass || !lesson || !formValues.date || !visitorFormValues.name.trim()) {
      return;
    }

    setIsSavingVisitor(true);
    setSaveVisitorError(null);

    try {
      await createVisitor({
        trimesterId: activeTrimesterId,
        date: formValues.date,
        name: visitorFormValues.name,
        whatsapp: visitorFormValues.whatsapp,
        notes: `Cadastro realizado na Lição ${lesson.lessonNumber}.`,
      });

      setVisitorFormValues(emptyVisitorFormValues);
      setIsVisitorModalOpen(false);
    } catch (error) {
      setSaveVisitorError(error instanceof Error ? error.message : 'Nao foi possivel registrar o visitante.');
    } finally {
      setIsSavingVisitor(false);
    }
  };

  const handleConfirmRemoveVisitor = async () => {
    if (!visitorToRemove) {
      return;
    }

    const visitorId = visitorToRemove.id;
    const visitorName = visitorToRemove.name;

    setRemovingVisitorId(visitorId);
    setSaveVisitorError(null);

    try {
      await removeVisitor(visitorId);
      setVisitorRemovalId(null);
      setVisitorNotice({
        type: 'success',
        message: `${visitorName} removido desta aula com sucesso.`,
      });
    } catch (error) {
      setVisitorNotice({
        type: 'error',
        message: error instanceof Error ? error.message : 'Nao foi possivel remover o visitante desta aula.',
      });
    } finally {
      setRemovingVisitorId(null);
    }
  };

  const handleConfirmConvertVisitorToStudent = async () => {
    if (!visitorToConvert) {
      return;
    }

    const visitorId = visitorToConvert.id;
    const visitorName = visitorToConvert.name;

    setConvertingVisitorId(visitorId);
    setSaveVisitorError(null);

    try {
      await convertVisitorToStudent(visitorId, formValues.date || todayIso);
      await reloadStudents();
      setVisitorConversionId(null);
      setVisitorNotice({
        type: 'success',
        message: `${visitorName} transformado em aluno com sucesso.`,
      });
    } catch (error) {
      setVisitorNotice({
        type: 'error',
        message: error instanceof Error ? error.message : 'Nao foi possivel transformar o visitante em aluno.',
      });
    } finally {
      setConvertingVisitorId(null);
    }
  };

  const handleSaveLessonRecord = async () => {
    if (!selectedClass || !lesson) {
      return;
    }

    setIsSavingLesson(true);

    try {
      const normalizedDate = formValues.date || todayIso;
      const savedLesson = await classLessonService.syncAttendance(selectedClass.id, lesson.id, {
        trimesterId: activeTrimesterId,
        date: normalizedDate,
        teacherName: formValues.teacherNames[0] ?? '',
        description: formValues.description.trim(),
        biblesCount: Math.floor(Math.max(0, Number(formValues.bibles) || 0)),
        offeringAmount: parseCurrencyInput(formValues.offeringAmount),
        isMissionsOffering: formValues.isMissionsOffering,
        notes: formValues.notes.trim(),
        attendances: selectedClassStudents.map((student) => ({
          studentId: student.id,
          present: (formValues.studentStatuses[student.id] ?? 'absent') === 'present',
        })),
      });

      setClassLessonRecords((current) => {
        const filtered = current.filter((item) => item.id !== savedLesson.id);
        return [...filtered, savedLesson].sort((left, right) => left.lessonNumber - right.lessonNumber);
      });
      setVisitorNotice({
        type: 'success',
        message: 'Registro da aula salvo com sucesso.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel salvar o registro da aula.';
      setVisitorNotice({
        type: 'error',
        message,
      });
    } finally {
      setIsSavingLesson(false);
    }
  };

  const backUrl = classId ? `/classes/${classId}/licoes?trimester_id=${activeTrimesterId}` : '/classes';

  if (isLoadingClassroom || isLoadingMagazines || isLoadingLessons || isLoadingStudents || isLoadingTeachers) {
    return (
      <MainLayout showBack onBack={() => navigate(backUrl)}>
        <p className="text-sm text-slate-500">Carregando lição...</p>
      </MainLayout>
    );
  }

  if (!selectedClass || !lesson) {
    return (
      <MainLayout showBack onBack={() => navigate(backUrl)}>
        <p className="text-sm text-slate-500">{classroomError ?? magazinesError ?? lessonError ?? 'Lição não encontrada.'}</p>
      </MainLayout>
    );
  }

  return (
    <MainLayout showBack onBack={() => navigate(backUrl)}>
      <div className="space-y-5">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">Lição {lesson.lessonNumber}</p>
          <h1 className="text-2xl font-bold text-slate-900">{formValues.title || `Lição ${lesson.lessonNumber}`}</h1>
          <p className="text-sm text-slate-500">{selectedClass.title}</p>
        </header>

        <Card className="border-slate-200 bg-white p-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[
              { label: 'Presentes', value: attendanceSummary.present, icon: CircleCheck, tone: 'bg-emerald-100 text-emerald-700' },
              { label: 'Faltas', value: attendanceSummary.absent, icon: AlertTriangle, tone: 'bg-rose-100 text-rose-700' },
              { label: 'Visitantes', value: lessonVisitors.length, icon: UserRoundPlus, tone: 'bg-violet-100 text-violet-700' },
              { label: 'Bíblias', value: biblesCount, icon: NotebookTabs, tone: 'bg-sky-100 text-sky-700' },
              { label: 'Oferta', value: formatCurrencyInput(offeringAmount), icon: DollarSign, tone: 'bg-amber-100 text-amber-700' },
            ].map(({ label, value, icon: Icon, tone }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className={cn('mb-3 inline-flex rounded-xl p-2', tone)}>
                  <Icon size={16} />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
              </div>
            ))}
          </div>
        </Card>

        <div className="rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <div className="grid grid-cols-2 gap-1">
            {[
              { id: 'summary' as const, label: 'Resumo e registros' },
              { id: 'attendance' as const, label: 'Presenças' },
            ].map((tab) => {
              const isActive = activeLessonTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveLessonTab(tab.id)}
                  className={cn(
                    'min-h-12 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
                    isActive ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeLessonTab === 'summary' && (
          <>
            <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <BookOpenCheck size={18} className="text-primary" />
                <p className="text-sm font-bold text-slate-900">Resumo da aula</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Título da aula"
                  value={formValues.title}
                  onChange={(event) => setFormValues((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Título da lição"
                />
                <Input
                  label="Data da aula"
                  type="date"
                  value={formValues.date}
                  onChange={(event) => setFormValues((current) => ({ ...current, date: event.target.value || todayIso }))}
                />
              </div>

              <SearchableSelect
                label="Professor da lição"
                placeholder={isLoadingTeachers ? 'Carregando professores...' : 'Selecione o professor'}
                searchPlaceholder="Buscar professor..."
                emptyMessage="Nenhum professor cadastrado para esta turma."
                options={teacherOptions}
                value={formValues.teacherNames}
                onChange={(values) => setFormValues((current) => ({ ...current, teacherNames: values.slice(-1) }))}
              />

              {teachersError && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                  {teachersError}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="ml-1 text-sm font-medium text-gray-700">Descrição / resumo</label>
                <textarea
                  value={formValues.description}
                  onChange={(event) => setFormValues((current) => ({ ...current, description: event.target.value }))}
                  placeholder="Resumo da aula, observações do conteúdo e encaminhamentos."
                  className="min-h-[120px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </Card>

            <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <ClipboardCheck size={18} className="text-emerald-600" />
                <p className="text-sm font-bold text-slate-900">Registro da aula</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Quantidade de Bíblias"
                  type="number"
                  min={0}
                  step={1}
                  value={formValues.bibles}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormValues((current) => ({ ...current, bibles: value === '' ? '' : String(Math.max(0, Number(value) || 0)) }));
                  }}
                  placeholder="0"
                />
                <Input
                  label="Valor da oferta"
                  inputMode="decimal"
                  value={formValues.offeringAmount}
                  onChange={(event) => setFormValues((current) => ({ ...current, offeringAmount: event.target.value }))}
                  onBlur={() =>
                    setFormValues((current) => ({
                      ...current,
                      offeringAmount: formatCurrencyInput(parseCurrencyInput(current.offeringAmount)),
                    }))
                  }
                  placeholder="R$ 0,00"
                />
              </div>

              <label className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  checked={formValues.isMissionsOffering}
                  onChange={(event) =>
                    setFormValues((current) => ({ ...current, isMissionsOffering: event.target.checked }))
                  }
                />
                <span className="text-sm">
                  <span className="block font-semibold text-amber-900">Oferta direcionada a Missões</span>
                  <span className="mt-0.5 block text-xs text-amber-700">
                    Quando marcado, 100% da oferta deste domingo é contabilizada como missões e o repasse de 20% para a sede não é aplicado.
                  </span>
                </span>
              </label>

              <div className="space-y-1.5">
                <label className="ml-1 text-sm font-medium text-gray-700">Observações da chamada</label>
                <textarea
                  value={formValues.notes}
                  onChange={(event) => setFormValues((current) => ({ ...current, notes: event.target.value }))}
                  placeholder="Observações da frequência, oferta ou acompanhamento."
                  className="min-h-[92px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </Card>
          </>
        )}

        {activeLessonTab === 'attendance' && (
          <>
            <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-slate-900">Visitantes da aula</p>
              <p className="mt-1 text-xs font-medium text-slate-500">
                {lessonVisitors.length} {lessonVisitors.length === 1 ? 'visitante registrado' : 'visitantes registrados'}
              </p>
            </div>
            <Button className="h-11 gap-2 px-3" onClick={() => setIsVisitorModalOpen(true)}>
              <UserRoundPlus size={16} />
              Visitante
            </Button>
          </div>

          {isLoadingVisitors ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
              Carregando visitantes da aula...
            </div>
          ) : lessonVisitors.length > 0 ? (
            <div className="space-y-2">
              {lessonVisitors.map((visitor) => (
                <div key={visitor.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{visitor.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{visitor.whatsapp || 'WhatsApp não informado'}</p>
                      {visitor.convertedToStudentId && (
                        <p className="mt-2 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                          Transformado em aluno
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-xl text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setVisitorConversionId(visitor.id)}
                        disabled={Boolean(visitor.convertedToStudentId) || convertingVisitorId === visitor.id}
                        title="Transformar em aluno"
                      >
                        <UserCheck size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-xl text-rose-600 hover:bg-rose-50"
                        onClick={() => setVisitorRemovalId(visitor.id)}
                        disabled={removingVisitorId === visitor.id || convertingVisitorId === visitor.id}
                        title="Remover visitante"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
              Nenhum visitante registrado nesta aula.
            </div>
          )}

          {visitorsError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
              {visitorsError}
            </div>
          )}
        </Card>

        <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-slate-900">Presenças</p>
              <p className="mt-1 text-xs font-medium text-slate-500">{selectedClassStudents.length} alunos vinculados</p>
            </div>
            <Button
              variant="ghost"
              className="h-11 gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700"
              onClick={() => handleFillAttendance('present')}
            >
              <CheckCheck size={16} />
              Todos
            </Button>
          </div>

          {selectedClassStudents.length > 0 ? (
            <div className="space-y-3">
              {selectedClassStudents.map((student) => {
                const currentStatus = formValues.studentStatuses[student.id] ?? 'absent';
                const isPresent = currentStatus === 'present';
                const ageMismatchLabel = getAgeMismatchLabel(student);

                return (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => handleAttendanceStatusChange(student.id, isPresent ? 'absent' : 'present')}
                    className="w-full text-left"
                  >
                    <Card
                      className={cn(
                        'space-y-3 border-slate-200 bg-white p-4 shadow-none transition-colors',
                        ageMismatchLabel && 'border-rose-200',
                        isPresent && 'border-emerald-300 bg-emerald-50/60'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{student.name}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                                {student.ageGroup}
                              </span>
                              <span
                                className={cn(
                                  'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                                  isPresent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                                )}
                              >
                                {isPresent ? 'Presente' : 'Ausente'}
                              </span>
                              {ageMismatchLabel && (
                                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                                  {ageMismatchLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <span
                          className={cn(
                            'inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors',
                            isPresent
                              ? 'border-emerald-200 bg-emerald-500 text-white'
                              : 'border-slate-200 bg-white text-slate-400'
                          )}
                        >
                          {isPresent ? <CircleCheckBig size={18} /> : <Circle size={18} />}
                        </span>
                      </div>
                    </Card>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
              Vincule alunos à turma antes de registrar a frequência desta lição.
            </div>
          )}
        </Card>
          </>
        )}

        <div className="sticky bottom-4 z-10">
          <Button
            className="h-12 w-full gap-2 rounded-2xl shadow-lg"
            onClick={handleSaveLessonRecord}
            disabled={isSavingLesson}
          >
            <CircleCheck size={18} />
            {isSavingLesson ? 'Salvando registro...' : 'Salvar registro da aula'}
          </Button>
        </div>
      </div>

      <FullScreenModal
        open={isVisitorModalOpen}
        onClose={() => setIsVisitorModalOpen(false)}
        title="Registrar visitante"
        subtitle={`Lição ${lesson.lessonNumber} • ${formatDateLabel(formValues.date)}`}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={() => setIsVisitorModalOpen(false)}>
              Cancelar
            </Button>
            <Button className="h-12 gap-2" onClick={handleSaveVisitor} disabled={isSavingVisitor || !visitorFormValues.name.trim()}>
              <UserRoundPlus size={18} />
              {isSavingVisitor ? 'Registrando...' : 'Registrar visitante'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {saveVisitorError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
              {saveVisitorError}
            </div>
          )}
          <Input
            label="Nome"
            value={visitorFormValues.name}
            onChange={(event) => setVisitorFormValues((current) => ({ ...current, name: event.target.value }))}
            placeholder="Nome do visitante"
          />
          <Input
            label="WhatsApp"
            value={visitorFormValues.whatsapp}
            onChange={(event) => setVisitorFormValues((current) => ({ ...current, whatsapp: event.target.value }))}
            placeholder="(00) 00000-0000"
          />
          <div className="rounded-3xl border border-sky-100 bg-sky-50 p-4">
            <p className="text-sm font-semibold text-sky-900">Cadastro feito na aula</p>
            <p className="mt-1 text-sm text-sky-800">
              A turma passa a exibir este visitante automaticamente pelo histórico de visitas.
            </p>
          </div>
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={visitorToConvert !== null}
        onClose={() => setVisitorConversionId(null)}
        title="Transformar em aluno"
        subtitle={visitorToConvert?.name}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              onClick={() => setVisitorConversionId(null)}
              disabled={convertingVisitorId !== null}
            >
              Cancelar
            </Button>
            <Button
              className="h-12 gap-2"
              onClick={handleConfirmConvertVisitorToStudent}
              disabled={convertingVisitorId !== null}
            >
              <UserCheck size={18} />
              {convertingVisitorId ? 'Transformando...' : 'Transformar em aluno'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Esta ação cadastra o visitante como aluno ativo desta turma e mantém o registro da visita na aula atual.
          </p>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
            Depois da confirmação, o aluno passa a aparecer na lista de presenças da turma.
          </div>
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={visitorToRemove !== null}
        onClose={() => setVisitorRemovalId(null)}
        title="Remover visitante"
        subtitle={visitorToRemove?.name}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              onClick={() => setVisitorRemovalId(null)}
              disabled={removingVisitorId !== null}
            >
              Cancelar
            </Button>
            <Button
              className="h-12 gap-2 bg-rose-600 hover:bg-rose-700"
              onClick={handleConfirmRemoveVisitor}
              disabled={removingVisitorId !== null}
            >
              <Trash2 size={18} />
              {removingVisitorId ? 'Removendo...' : 'Remover desta aula'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Esta ação remove o visitante apenas desta aula. Registros de visitas anteriores permanecem preservados.
          </p>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
            Confirme somente se este visitante não participou da lição atual.
          </div>
        </div>
      </FullScreenModal>

      <Toast notice={visitorNotice} onClose={() => setVisitorNotice(null)} />
    </MainLayout>
  );
};

export default ClassLessonDetailsPage;
